/* eslint-disable @next/next/no-img-element */
'use client';

import { motion } from 'motion/react';
import React from 'react';

import { CMSLink } from '@/components/Link';
import { Slider } from '@/components/Slider';
import type { Media, QuotesBlock as QuotesBlockProps } from '@/payload-types';
import { cn } from '@/utils/cn';

import { BlockSettings } from '../BlockSettings';
import styles from './block.module.scss';

const itemVariants = {
  hidden: { opacity: 0, y: 50 },
  visible: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: 50 },
};

export const QuotesBlock: React.FC<QuotesBlockProps> = (props) => {
  const {
    backgroundColor,
    heading,
    individualSpacing,
    quotes,
    spacing,
    spacingBottom,
    spacingTop,
    tracking,
  } = props;

  if (!quotes || !Array.isArray(quotes) || quotes.length === 0) {
    return null;
  }

  return (
    <BlockSettings
      backgroundColor={backgroundColor}
      individualSpacing={individualSpacing}
      spacing={spacing}
      spacingBottom={spacingBottom}
      spacingTop={spacingTop}
    >
      <div className={cn(styles.wrapper, 'container')}>
        <Slider label={heading} divider showListControlsText>
          {quotes.map((item, index) => {
            const { author, byline, link, logo, quote } = item;

            if (!quote) {
              return null;
            }

            return (
              <motion.div
                key={index}
                variants={itemVariants}
                style={{ willChange: 'opacity, transform' }}
                transition={{ duration: 0.3 }}
                className={styles.quote}
              >
                <div className={styles.quoteContent}>
                  {logo && (logo as Media).url && (
                    <div className={styles.quoteLogo}>
                      <img
                        loading='lazy'
                        src={(logo as Media).url as string}
                        alt={(logo as Media)?.alt || ''}
                      />
                    </div>
                  )}
                  <div className={styles.quoteText}>{quote}</div>
                  <div className={styles.quoteBy}>
                    {author && (
                      <span className={styles.quoteAuthor}>{author}</span>
                    )}
                    {byline && <span>{byline}</span>}
                  </div>
                  {link && (
                    <div className={styles.quoteLink}>
                      <CMSLink
                        {...link}
                        className={styles.quoteLink}
                        appearance='level-2'
                        tracking={{
                          type: 'Quote',
                          headline: tracking?.cta || heading || '',
                          cta: link?.label || '',
                        }}
                      />
                    </div>
                  )}
                </div>
              </motion.div>
            );
          })}
        </Slider>
      </div>
    </BlockSettings>
  );
};
